import { type ReactNode, useState, useMemo } from 'react';
import cx from 'clsx';
import { PdfPreview } from '@repo/domain-kit/invoices';
import type { Client, Merchant, DocumentType, Document, AmountOrString } from '../types';
import styles from '../responsive-view.module.css';

interface PdfSectionProps {
  document: Document;
  client?: Client;
  merchant: Merchant;
  documentType: DocumentType;
}

/**
 * Transforms Amount objects with currency and value to string values
 * for compatibility with domain-kit's PdfPreview component
 */
function transformDocumentForPdfPreview(document: Document): unknown {
  const transformAmount = (amount: AmountOrString | undefined): string | undefined => {
    if (!amount) return undefined;
    if (typeof amount === 'string') return amount;
    return amount.value;
  };

  return {
    ...document,
    // Transform document-level amounts
    subtotal: transformAmount(document.subtotal),
    vatAmount: transformAmount(document.vatAmount),
    amountDue: transformAmount(document.amountDue),
    stampDutyAmount: transformAmount(document.stampDutyAmount),
    welfareFundAmount: transformAmount(document.welfareFundAmount),
    withholdingTaxAmount: transformAmount(document.withholdingTaxAmount),
    // Map receivable-invoices-js fields to PdfPreview expected fields
    discountedTotalExcludingVat: transformAmount(document.subtotal), // subtotal is the total excluding VAT
    totalVat: transformAmount(document.vatAmount), // vatAmount is the total VAT
    totalAmount: transformAmount(document.amountDue), // amountDue is the total including VAT
    // Transform section items
    sections: document.sections?.map(section => ({
      ...section,
      items: section.items.map(item => {
        // Access subtotal with correct casing (could be 'subtotal' or 'subTotal')
        const itemWithSubtotal = item as Record<string, unknown>;
        const subtotalValue = (itemWithSubtotal.subtotal as AmountOrString) || item.subTotal;
        return {
          ...item,
          unitPrice: transformAmount(item.unitPrice),
          totalVat: transformAmount(item.totalVat),
          totalAmount: transformAmount(item.totalAmount),
          subTotal: transformAmount(subtotalValue),
          // Map subtotal to totalExcludingVat (PdfPreview expects this field)
          totalExcludingVat:
            transformAmount(item.totalExcludingVat) || transformAmount(subtotalValue),
        };
      }),
    })),
    // Transform standalone items (for credit notes)
    items: document.items?.map(item => {
      // Access subtotal with correct casing (could be 'subtotal' or 'subTotal')
      const itemWithSubtotal = item as Record<string, unknown>;
      const subtotalValue = (itemWithSubtotal.subtotal as AmountOrString) || item.subTotal;
      return {
        ...item,
        unitPrice: transformAmount(item.unitPrice),
        totalVat: transformAmount(item.totalVat),
        totalAmount: transformAmount(item.totalAmount),
        subTotal: transformAmount(subtotalValue),
        // Map subtotal to totalExcludingVat (PdfPreview expects this field)
        totalExcludingVat:
          transformAmount(item.totalExcludingVat) || transformAmount(subtotalValue),
      };
    }),
  };
}

export function PdfSection({
  document,
  client,
  merchant,
  documentType,
}: PdfSectionProps): ReactNode {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const transformedDocument = useMemo(
    () => transformDocumentForPdfPreview(document) as Parameters<typeof PdfPreview>[0]['document'],
    [document]
  );

  if (document.pdfSrc) {
    return (
      <div className={styles.pdfPreview} data-testid="pdf-section-iframe-container">
        <iframe
          className={cx(styles.iframe, !isIframeLoaded && styles.hidden)}
          data-test-pdf-iframe
          data-testid="pdf-section-iframe"
          onLoad={() => {
            setIsIframeLoaded(true);
          }}
          src={`${document.pdfSrc}#toolbar=0&view=Fit`}
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- title required for iframe
          title="pdf-preview"
        />
      </div>
    );
  }

  return (
    <div data-testid="pdf-section-preview-container">
      <PdfPreview
        client={client}
        document={transformedDocument}
        logo={undefined}
        merchant={merchant}
        relatedInvoice={document.originalInvoice}
        type={documentType}
        variant={merchant.legalCountry}
      />
    </div>
  );
}
